import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList, StructuredData } from 'components/SEO';
import { useGetPublicSetsQuery } from 'store/services/sets/publicSetsApi';
import { trackEvent } from 'utils/analytics';
import SetCard from './components/SetCard';
import './SetsIndexPage.css';

// OG image for social sharing
const SETS_OG_IMAGE =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/og-images/sets-directory.jpg';

const SetsIndexPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Fetch all sets
  const { data: setsData, isLoading, error, refetch } = useGetPublicSetsQuery();

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Client-side filtering with useMemo for performance
  const filteredSets = useMemo(() => {
    if (!setsData?.sets) return [];
    if (!searchQuery.trim()) return setsData.sets;

    const query = searchQuery.toLowerCase().trim();
    return setsData.sets.filter(
      (set) =>
        set.name.toLowerCase().includes(query) ||
        set.code.toLowerCase().includes(query)
    );
  }, [setsData?.sets, searchQuery]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle sidebar navigation attempts
  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Handle search input with analytics
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Track search when user types at least 3 characters
    if (query.length >= 3) {
      trackEvent('sets_searched', { query });
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Handle set click with analytics
  const handleSetClick = (setCode: string) => {
    trackEvent('set_viewed', { setCode });
    navigate(`/sets/${setCode.toLowerCase()}`);
  };

  // CollectionPage schema for SEO
  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Magic: The Gathering Sets Directory',
    description: 'Complete directory of all Magic: The Gathering sets on TradeMagic',
    url: 'https://trademagic.gg/sets',
    publisher: {
      '@type': 'Organization',
      name: 'TradeMagic',
      url: 'https://trademagic.gg',
    },
    numberOfItems: setsData?.totalCount || 0,
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title="MTG Sets Directory - Browse All Magic Sets | TradeMagic"
        description="Browse all Magic: The Gathering sets from Alpha to the latest releases. Find cards from your favorite sets and trade with collectors worldwide. Free peer-to-peer trading platform."
        canonical="https://trademagic.gg/sets"
        image={SETS_OG_IMAGE}
        keywords="MTG sets, Magic The Gathering sets, MTG expansions, Magic card sets, browse sets, TradeMagic"
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Sets', url: 'https://trademagic.gg/sets' },
        ]}
      />

      {/* CollectionPage Schema */}
      <StructuredData data={collectionPageSchema} />

      <div className="sets-index-layout">
        {/* Mobile Menu Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="sets-index-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="sets-index-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/sets', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="sets-index-main">
            {/* Top Navigation Bar */}
            <TopNavigationBar
              title="Sets"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="sets-index-container">
              {/* Header */}
              <header className="sets-index-header">
                <h1 className="sets-index-title">Magic: The Gathering Sets</h1>
                <p className="sets-index-subtitle">
                  Browse all {setsData?.totalCount?.toLocaleString() || 0} sets
                </p>
              </header>

              {/* Search Bar */}
              <div className="sets-index-search-container">
                <input
                  type="search"
                  placeholder="Search sets by name or code..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="sets-index-search-input"
                  aria-label="Search Magic sets"
                />
                {searchQuery && (
                  <button
                    className="sets-index-search-clear"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Content */}
              {isLoading ? (
                // Skeleton loading state
                <div className="sets-index-grid">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className="sets-index-skeleton">
                      <div className="sets-index-skeleton-icon" />
                      <div className="sets-index-skeleton-content">
                        <div className="sets-index-skeleton-title" />
                        <div className="sets-index-skeleton-meta" />
                        <div className="sets-index-skeleton-footer" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : error ? (
                // Error state
                <div className="sets-index-error">
                  <p>Unable to load sets. Please try again.</p>
                  <button onClick={() => refetch()} className="sets-index-retry-btn">
                    Retry
                  </button>
                </div>
              ) : (
                <>
                  {/* Count */}
                  <div className="sets-index-count">
                    {filteredSets.length === setsData?.sets?.length
                      ? `${filteredSets.length.toLocaleString()} sets`
                      : `${filteredSets.length.toLocaleString()} of ${setsData?.sets?.length.toLocaleString()} sets`}
                  </div>

                  {/* Empty state for search */}
                  {filteredSets.length === 0 && searchQuery ? (
                    <div className="sets-index-empty">
                      <p>No sets found matching "{searchQuery}"</p>
                      <button
                        onClick={handleClearSearch}
                        className="sets-index-clear-btn"
                      >
                        Clear search
                      </button>
                    </div>
                  ) : (
                    // Sets grid
                    <div className="sets-index-grid">
                      {filteredSets.map((set) => (
                        <SetCard
                          key={set.code}
                          set={set}
                          onClick={() => handleSetClick(set.code)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default SetsIndexPage;
